import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, Badge, Button, Alert } from '../components/design-system';
import { useAuth } from '../contexts/AuthContext';
import {
  Rss, Inbox, Layers, FileText, Key, TrendingUp,
  CheckCircle, Clock, AlertCircle, Eye, ArrowRight, Play
} from 'lucide-react';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [trending, setTrending] = useState([]);
  const [sourceHealth, setSourceHealth] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineMsg, setPipelineMsg] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, trendRes, healthRes, actRes] = await Promise.all([
        api.getStats(),
        api.getTrending(),
        api.getSourceHealth(),
        api.getRecentActivity(),
      ]);
      setStats(statsRes.stats);
      setTrending(trendRes.trending || []);
      setSourceHealth(healthRes.sources || []);
      setActivity(actRes.activity || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const statCards = stats ? [
    { label: 'Active Sources', value: stats.sources?.active || 0, total: stats.sources?.total, icon: Rss, color: 'text-blue-600 bg-blue-50', link: '/sources' },
    { label: 'News Items Today', value: stats.newsItems?.today || 0, total: stats.newsItems?.total, icon: Inbox, color: 'text-purple-600 bg-purple-50', link: '/feed' },
    { label: 'Active Clusters', value: stats.clusters?.active || 0, icon: Layers, color: 'text-amber-600 bg-amber-50', link: '/clusters' },
    { label: 'Published Articles', value: stats.articles?.published || 0, total: stats.articles?.total, icon: FileText, color: 'text-primary-600 bg-primary-50', link: '/articles' },
    { label: 'Pending Review', value: stats.articles?.pendingReview || 0, icon: Clock, color: 'text-orange-600 bg-orange-50', link: '/articles?status=review' },
    { label: 'Active API Keys', value: stats.apiKeys?.active || 0, icon: Key, color: 'text-gray-600 bg-gray-100', link: '/api-keys' },
  ] : [];

  const handleRunPipeline = async () => {
    setPipelineRunning(true); setPipelineMsg(null);
    try {
      const res = await api.runPipeline();
      setPipelineMsg({ type: 'success', text: res.message || 'Pipeline completed' });
      loadDashboard();
    } catch (err) { setPipelineMsg({ type: 'error', text: err.message }); }
    finally { setPipelineRunning(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Pipeline control */}
      {isAdmin && (
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Pipeline:</span> Fetch → Analyze → Cluster → Generate → Cleanup
          </div>
          <Button icon={Play} size="sm" loading={pipelineRunning} onClick={handleRunPipeline}>
            Run Pipeline
          </Button>
        </div>
      )}
      {pipelineMsg && <Alert type={pipelineMsg.type} onClose={() => setPipelineMsg(null)}>{pipelineMsg.text}</Alert>}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} hover onClick={() => navigate(s.link)} className="!p-4">
              <div className={`inline-flex p-2 rounded-lg ${s.color} mb-3`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {s.label}
                {s.total !== undefined && <span className="text-gray-400"> / {s.total} total</span>}
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Trending Stories */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary-500" />
              Trending Stories
            </h2>
            <button onClick={() => navigate('/clusters')} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          {trending.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No trending stories yet. Add sources to get started.</p>
          ) : (
            <div className="space-y-3">
              {trending.slice(0, 5).map((cluster) => (
                <div
                  key={cluster.id}
                  className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/clusters/${cluster.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{cluster.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="primary">{cluster.source_count} sources</Badge>
                      <Badge>{cluster.item_count} items</Badge>
                      <span className="text-xs text-gray-400">{formatTimeAgo(cluster.last_updated_at)}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-medium text-primary-600">
                      {Math.round((cluster.importance_score || 0) * 100)}%
                    </div>
                    <div className="text-[10px] text-gray-400">importance</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Source Health */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Rss className="h-4 w-4 text-blue-500" />
              Source Health
            </h2>
            <button onClick={() => navigate('/sources')} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
              Manage <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          {sourceHealth.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No sources configured yet.</p>
          ) : (
            <div className="space-y-2">
              {sourceHealth.slice(0, 8).map((source) => {
                const isHealthy = source.last_status === 'success';
                const isStale = source.last_fetched_at && 
                  (Date.now() - new Date(source.last_fetched_at).getTime()) > (source.fetch_interval_minutes || 15) * 60 * 1000 * 2;
                return (
                  <div
                    key={source.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/sources/${source.id}`)}
                  >
                    {isHealthy ? (
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : source.last_status === 'error' ? (
                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    ) : (
                      <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-sm text-gray-700 truncate flex-1">{source.name}</span>
                    <Badge variant={source.type === 'rss' ? 'info' : source.type === 'scrape' ? 'warning' : 'default'}>
                      {source.type}
                    </Badge>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {source.items_fetched_total || 0} items
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Recent Activity</h2>
        </div>
        {activity.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No recent activity.</p>
        ) : (
          <div className="space-y-2">
            {activity.slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-sm py-1.5">
                <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-medium text-gray-500">
                    {(a.display_name?.[0] || a.email?.[0] || '?').toUpperCase()}
                  </span>
                </div>
                <span className="text-gray-500 flex-1">
                  <span className="font-medium text-gray-700">{a.display_name || a.email || 'System'}</span>
                  {' '}{formatAction(a.action)}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">{formatTimeAgo(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatAction(action) {
  const actions = {
    source_created: 'added a new source',
    source_updated: 'updated a source',
    source_deleted: 'deleted a source',
    source_fetch_triggered: 'triggered a source fetch',
    article_published: 'published an article',
    article_approved: 'approved an article',
    article_rejected: 'rejected an article',
    article_updated: 'edited an article',
    user_registered: 'joined the newsdesk',
    user_role_updated: 'changed a user role',
    settings_updated: 'updated settings',
    api_key_created: 'created an API key',
    news_item_discarded: 'discarded a news item',
  };
  return actions[action] || action.replace(/_/g, ' ');
}
